import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { Role } from '../../types';
import {
  UserPlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { SuperAdminService, UserProfile } from '../../services/superAdminService';
import CreateCompanyAdminModal from '../../components/admin/CreateCompanyAdminModal';
import EditCompanyAdminModal from '../../components/admin/EditCompanyAdminModal';
import logger from '../../utils/logger';
import Button from '../../components/ui/Button';
import PageTitle from '../../components/ui/PageTitle';
import { useToast } from '../../hooks/useToast';
import { useDebounce } from '../../hooks/useDebounce';
import { Alert } from '../../components/ui';

const CompanyAdminManagement: React.FC = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<UserProfile[]>([]);
  const [filteredAdmins, setFilteredAdmins] = useState<UserProfile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAdminId, setEditingAdminId] = useState<string>('');
  const [error, setError] = useState<string>('');

  const superAdminService = React.useMemo(() => new SuperAdminService(), []);
  const toast = useToast();

  // 検索パフォーマンス最適化: 300ms遅延でフィルタリング実行
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // 管理者一覧取得
  const loadAdmins = async () => {
    setIsLoading(true);
    setError('');
    try {
      const allUsers = await superAdminService.getAllUsers();
      // admin ロールのユーザーのみフィルタ
      const adminUsers = allUsers.filter(u => u.role === 'admin');
      setAdmins(adminUsers);
      setFilteredAdmins(adminUsers);
    } catch (err: any) {
      logger.error('Failed to load admins:', err);
      setError('管理者一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  // 検索フィルタ: debounceされた検索クエリを使用
  useEffect(() => {
    if (!debouncedSearchQuery) {
      setFilteredAdmins(admins);
      return;
    }

    const query = debouncedSearchQuery.toLowerCase();
    const filtered = admins.filter(
      admin =>
        admin.name.toLowerCase().includes(query) ||
        admin.email.toLowerCase().includes(query) ||
        admin.company_name?.toLowerCase().includes(query)
    );
    setFilteredAdmins(filtered);
  }, [debouncedSearchQuery, admins]);

  // Super Admin権限チェック
  if (!user || user.role !== Role.SUPER_ADMIN) {
    return <Navigate to='/' replace />;
  }

  const handleCreateSuccess = async () => {
    setIsCreateModalOpen(false);
    await loadAdmins();
  };

  const handleEdit = (adminId: string) => {
    setEditingAdminId(adminId);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = async () => {
    setIsEditModalOpen(false);
    setEditingAdminId('');
    await loadAdmins();
  };

  const handleDelete = async (adminId: string, adminName: string) => {
    if (!window.confirm(`${adminName} を削除してもよろしいですか？\nこの操作は取り消せません。`)) {
      return;
    }

    try {
      await superAdminService.deleteUser(adminId);
      await loadAdmins();
    } catch (err: any) {
      logger.error('Failed to delete admin:', err);
      setError(`削除に失敗しました: ${err.message}`);
    }
  };

  const handlePasswordReset = async (email: string, adminName: string) => {
    if (!window.confirm(`${adminName} (${email}) のパスワードリセットメールを送信しますか？`)) {
      return;
    }

    try {
      await superAdminService.resetCompanyAdminPassword(email);
      toast.success(`${email} 宛にパスワードリセットメールを送信しました`, {
        title: 'パスワードリセット',
      });
    } catch (err: any) {
      logger.error('Failed to reset password:', err);
      setError(`パスワードリセットに失敗しました: ${err.message}`);
    }
  };

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
      <PageTitle icon={UserPlusIcon} title='陸送会社管理者管理' />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {error && (
          <div className='mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
            <p className='text-sm text-red-800 dark:text-red-200'>{error}</p>
          </div>
        )}

        {/* ツールバー */}
        <div className='bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-6'>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0'>
            {/* 検索バー */}
            <div className='relative flex-1 max-w-md'>
              <label htmlFor='admin-search' className='sr-only'>
                管理者を検索
              </label>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <MagnifyingGlassIcon
                  className='h-5 w-5 text-gray-400 dark:text-gray-500'
                  aria-hidden='true'
                />
              </div>
              <input
                id='admin-search'
                type='text'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder='名前、メール、会社名で検索...'
                aria-label='管理者を名前、メール、会社名で検索'
                className='block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm'
              />
            </div>

            {/* 新規作成ボタン */}
            <Button
              variant='primary'
              onClick={() => setIsCreateModalOpen(true)}
              icon={<UserPlusIcon className='h-5 w-5' />}
              aria-label='新しい管理者を作成'
            >
              新規作成
            </Button>
          </div>
        </div>

        {/* 管理者一覧テーブル */}
        <div className='bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden'>
          {isLoading ? (
            <div className='text-center py-12'>
              <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600'></div>
              <p className='mt-2 text-sm text-gray-500 dark:text-gray-400'>読み込み中...</p>
            </div>
          ) : filteredAdmins.length === 0 ? (
            <div className='text-center py-12'>
              <p className='text-gray-500 dark:text-gray-400'>
                {searchQuery
                  ? '検索条件に一致する管理者が見つかりません'
                  : '管理者が登録されていません'}
              </p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
                <thead className='bg-gray-50 dark:bg-gray-900'>
                  <tr>
                    <th
                      scope='col'
                      className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'
                    >
                      管理者名
                    </th>
                    <th
                      scope='col'
                      className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'
                    >
                      メールアドレス
                    </th>
                    <th
                      scope='col'
                      className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'
                    >
                      会社名
                    </th>
                    <th
                      scope='col'
                      className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'
                    >
                      作成日
                    </th>
                    <th scope='col' className='relative px-6 py-3'>
                      <span className='sr-only'>操作</span>
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                  {filteredAdmins.map(admin => (
                    <tr key={admin.id} className='hover:bg-gray-50 dark:hover:bg-gray-700'>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='text-sm font-medium text-gray-900 dark:text-white'>
                          {admin.name}
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='text-sm text-gray-500 dark:text-gray-400'>
                          {admin.email}
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='text-sm text-gray-500 dark:text-gray-400'>
                          {admin.company_name || '未設定'}
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                        {new Date(admin.created_at).toLocaleDateString('ja-JP')}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                        <div className='flex items-center justify-end space-x-2'>
                          <button
                            onClick={() => handlePasswordReset(admin.email, admin.name)}
                            className='text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300'
                            title='パスワードリセット'
                            aria-label={`${admin.name}のパスワードをリセット`}
                          >
                            <KeyIcon className='h-5 w-5' aria-hidden='true' />
                          </button>
                          <button
                            onClick={() => handleEdit(admin.id)}
                            className='text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300'
                            title='編集'
                            aria-label={`${admin.name}を編集`}
                          >
                            <PencilIcon className='h-5 w-5' aria-hidden='true' />
                          </button>
                          <button
                            onClick={() => handleDelete(admin.id, admin.name)}
                            className='text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                            title='削除'
                            aria-label={`${admin.name}を削除`}
                          >
                            <TrashIcon className='h-5 w-5' aria-hidden='true' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 統計情報 */}
        <div className='mt-6 bg-white dark:bg-gray-800 shadow rounded-lg p-6'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-4'>統計情報</h3>
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
            <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4'>
              <p className='text-sm text-gray-500 dark:text-gray-400'>総管理者数</p>
              <p
                className='text-2xl font-bold text-gray-900 dark:text-white'
                aria-label={`総管理者数: ${admins.length}人`}
              >
                {admins.length}
              </p>
            </div>
            <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4'>
              <p className='text-sm text-gray-500 dark:text-gray-400'>表示中</p>
              <p
                className='text-2xl font-bold text-gray-900 dark:text-white'
                aria-label={`表示中の管理者: ${filteredAdmins.length}人`}
              >
                {filteredAdmins.length}
              </p>
            </div>
            <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4'>
              <p className='text-sm text-gray-500 dark:text-gray-400'>検索フィルタ</p>
              <p
                className='text-2xl font-bold text-gray-900 dark:text-white'
                aria-label={
                  searchQuery
                    ? `検索フィルタ適用中: ${filteredAdmins.length}件該当`
                    : '検索フィルタなし'
                }
              >
                {searchQuery ? `${filteredAdmins.length}件` : 'なし'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 新規作成モーダル */}
      <CreateCompanyAdminModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* 編集モーダル */}
      {editingAdminId && (
        <EditCompanyAdminModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingAdminId('');
          }}
          onSuccess={handleEditSuccess}
          adminId={editingAdminId}
        />
      )}

      {/* トースト通知 */}
      <div className='fixed top-4 right-4 z-50 space-y-2'>
        {toast.toasts.map(t => (
          <Alert
            key={t.id}
            variant={t.variant}
            title={t.title}
            dismissible
            onDismiss={() => toast.removeToast(t.id)}
          >
            {t.message}
          </Alert>
        ))}
      </div>
    </div>
  );
};

export default CompanyAdminManagement;
